import React, { useEffect, useRef, useState } from 'react';

type StickyChildren<T extends HTMLElement> = (
  isSticky: boolean,
  wrapperRef: React.RefObject<T | null>
) => React.ReactNode;

interface StickyElementProps<T extends HTMLDivElement> {
  children: StickyChildren<T>;
  bottomPos?: boolean;
  topOffset?: number;
  bottomOffset?: number;
  elementIdToObserve?: string;
}

export const StickyElement = <T extends HTMLDivElement>({
  children,
  bottomPos = false,
  topOffset = 0,
  bottomOffset = 0,
  elementIdToObserve
}: StickyElementProps<T>) => {
  const wrapperRef = useRef<T>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isIphone, setIsIphone] = useState(false);

  useEffect(() => {
    setIsIphone(/iPhone/.test(navigator.userAgent) && !window.MSStream);
  }, []);

  // it fixes extra padding in iPhone safari
  const additionalBottomSpace = isIphone ? 60 : 0;

  useEffect(() => {
    const wrapperEl = wrapperRef.current;
    const windowHeight = window.innerHeight;
    const elementToObserve =
      document.getElementById(elementIdToObserve ?? '') || document.body;
    let isTimeToSet: boolean;

    const checkPosition = () => {
      const wrapperElBounds = wrapperEl?.getBoundingClientRect();

      if (!bottomPos) {
        isTimeToSet = (wrapperElBounds?.y ?? 0) - topOffset < 1;
      } else {
        isTimeToSet =
          (wrapperElBounds?.y ?? 0) + (wrapperElBounds?.height ?? 0) >
          windowHeight + additionalBottomSpace + bottomOffset;
      }

      setIsSticky(isTimeToSet);
    };

    checkPosition();

    if (elementToObserve && window.ResizeObserver) {
      const { ResizeObserver } = window; // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver/observe
      const observer = new ResizeObserver(() => {
        checkPosition();
      });
      observer.observe(elementToObserve);
    }

    window.addEventListener('scroll', checkPosition);
    window.addEventListener('resize', checkPosition);
    return () => {
      window.removeEventListener('scroll', checkPosition);
      window.removeEventListener('resize', checkPosition);
    };
  }, []);

  return children(isSticky, wrapperRef);
};

StickyElement.displayName = 'StickyElement';
